import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { setAccessToken } from '@drifthq/web-auth'

import config from 'config'
import { Flow } from 'constants/flowConstants'
import * as actions from './actions'
import * as selectors from './selectors'
import * as ProfileActions from 'modules/profile/actions'
import * as OrgActions from 'modules/org/actions'
import * as MeetingsActions from 'modules/meetings/actions'
import { api } from './api'

import { identifyUser } from 'utils/trackingUtils'

const fetchClearbitForEmail = action$ => {
  return action$
    .ofType(actions.fetchClearbitForEmail.toString())
    .pluck('payload')
    .mergeMap(email => {
      return api.fetchClearbitForEmail(email)
        .map(actions.fetchClearbitForEmailSuccess)
        .startWith(actions.fetchClearbitForEmailPending({ email }))
        .catch(error => {
          return Observable.of(actions.fetchClearbitForEmailFailed({ email, error }))
        })
    })
}

const checkUniqueEmail = action$ => {
  return action$
    .ofType(actions.checkUniqueEmail.toString())
    .debounceTime(200)
    .pluck('payload')
    .mergeMap(email => {
      return api
        .checkUniqueEmail(email)
        .map(actions.checkUniqueEmailFailed)
        .catch(error => {
          if (error.status === 404) {
            return Observable.of(actions.checkUniqueEmailSuccess())
          }
          return Observable.of(actions.checkUniqueEmailFailed())
        })
    })
}

const updatePassword = (action$, store) => {
  return action$
    .ofType(actions.updatePassword.toString())
    .filter(() => !selectors.isRepInvitedFlow(store.getState()) && !selectors.isCalendarSeatOnboarding(store.getState()))
    .mergeMap(() => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      const password = selectors.getPassword(state)
      return api
        .updatePassword(userId, password)
        .map(res => {
          setAccessToken({token: res.access_token})
          return actions.updatePasswordSuccess()
        })
        .startWith(actions.updatePasswordPending())
        .catch(() => Observable.of(actions.updatePasswordFailed()))
    })
}

const updateInvitedRepPassword = (action$, store) => {
  return action$
    .ofType(actions.updatePassword.toString())
    .filter(() => selectors.isRepInvitedFlow(store.getState()) || selectors.isCalendarSeatOnboarding(store.getState()))
    .mergeMap(() => {
      const state = store.getState()
      const password = selectors.getPassword(state)
      const token = selectors.getResetToken(state)
      return api
        .acceptInvitation(password, token)
        .map(res => {
          setAccessToken({token: res.access_token})
          return actions.updatePasswordSuccess()
        })
        .startWith(actions.updatePasswordPending())
        .catch(() => Observable.of(actions.updatePasswordFailed()))
    })
}

const redirectToHyfy = (action$, store) => {
  return action$
    .ofType(actions.updatePasswordSuccess.toString())
    .filter(() => selectors.getFlow(store.getState()) === Flow.VIDEO)
    .do(() => window.location.replace(config.HYFY_INVITE_REDIRECT_URL))
    .ignoreElements()
}

const fetchUserId = (action$, store) => {
  return action$
    .ofType(actions.updatePasswordSuccess.toString())
    .filter(() => selectors.isRepInvitedFlow(store.getState()) || selectors.isCalendarSeatOnboarding(store.getState()))
    .mergeMap(() => {
      return api
        .fetchUser()
        .map(res => actions.fetchUserSuccess(res))
        .startWith(actions.fetchUserPending())
        .catch(() => Observable.of(actions.fetchUserFailed()))
    })
}

const fetchUser = (action$, store) => {
  return action$
    .ofType(actions.fetchUser.toString())
    .mergeMap(() => {
      return api
        .fetchUser()
        .map(res => actions.fetchUserSuccess(res))
        .startWith(actions.fetchUserPending())
        .catch(() => Observable.of(actions.fetchUserFailed()))
    })
}

const updateStagedChangesForInvitedRep = (action$, store) => {
  return action$
    .ofType(actions.fetchUserSuccess.toString())
    .filter(() => selectors.isRepInvitedFlow(store.getState()))
    .flatMap(() => {
      const state = store.getState()
      const file = selectors.getProfilePicture(state)
      return [
        actions.updateUser(),
        ProfileActions.createProfile(),
        ProfileActions.uploadCoverPhoto(file)
      ]
    })
}

const identifyDriftUserForInvitedRep = (action$, store) => {
  return action$
    .ofType(OrgActions.fetchOrgSuccess.toString())
    .flatMap(() => {
      identifyUser()
      return Observable.empty()
    })
}

const updateUser = (action$, store) => {
  return action$
    .ofType(actions.updateUser.toString())
    .pluck('payload')
    .mergeMap(properties => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      return api
        .updateUser(userId, properties)
        .map(actions.updateUserSuccess)
        .startWith(actions.updateUserPending())
        .catch(() => Observable.of(actions.updateUserFailed()))
    })
}

const updateProfileWithPicture = (action$, store) => {
  return action$
    .ofType(ProfileActions.uploadCoverPhotoSuccess.toString())
    .pluck('payload')
    .mergeMap(url => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      return api
        .updateUser(userId, { avatarUrl: url })
        .map(() => actions.updateUserAvatarSuccess())
        .startWith(actions.updateUserAvatarPending())
        .catch((err) => Observable.of(actions.updateUserAvatarFailed()))
    })
}

const updateProfileWithCalendarSetting = (action$, store) => {
  return action$
    .ofType(
      MeetingsActions.updateMicrosoftCalendarSuccess.toString(),
      MeetingsActions.updateGoogleCalendarSuccess.toString()
    )
    .mergeMap(url => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      return api
        .updateUser(userId, { enableCalendarCta: true })
        .map(() => ProfileActions.updateProfileSuccess())
        .startWith(ProfileActions.updateProfilePending())
        .catch((err) => Observable.of(ProfileActions.updateProfileFailed()))
    })
}

const updateUserProperties = (action$, store) => {
  return action$
    .ofType(actions.updateUserProperties.toString())
    .mergeMap(properties => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      return api
        .updateUserProperties(userId, properties.payload)
        .catch((err) => Observable.of(actions.updateUserPropertiesFailed(err)))
    })
}

const getLoginUrl = (action$, store) => {
  return action$
    .ofType(actions.getLoginUrl.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      return api
        .getLoginUrl(email)
        .then((resp) => {
          window.location.replace(resp.url)
        })
        .catch((err) => actions.getLoginUrlFailed(err))
    })
}

const checkEmailIsSSOEnabled = (action$, store) => {
  return action$
    .ofType(actions.checkEmailIsSSOEnabled.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      return api
        .checkEmailIsSSOEnabled(email)
        .map((resp) => actions.checkEmailIsSSOEnabledSuccess(resp))
        .catch(() => Observable.of(actions.checkEmailIsSSOEnabledFailed()))
        .startWith(actions.checkEmailIsSSOEnabledPending())
    })
}

const checkEmailIsSSOEnabledSuccess = (action$, store) => {
  return action$
    .ofType(actions.checkEmailIsSSOEnabledSuccess.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      const password = selectors.getPassword(state)
      const emailIsSSOEnabled = selectors.emailIsSSOEnabled(state)
      if(!emailIsSSOEnabled) {
        return Observable.of(OrgActions.createOrg({
          email,
          password,
          anonymousId: window.analytics.user ? window.analytics.user().anonymousId() : undefined,
          enableDefaultWelcomeMessages: true
        }))
      } else {
        return Observable.of(actions.getLoginUrl())
      }
    })
}

const findAssociatedDomain = (action$, store) => {
  return action$
    .ofType(actions.findAssociatedDomain.toString())
    .debounceTime(500)
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      return api
        .findAssociatedDomain(email)
        .map((response) => actions.setAssociatedDomainFound(true))
        .catch((err) => Observable.of(actions.setAssociatedDomainFound(false)))
    })
}

const sendFindMyTeamVerificationEmail = (action$, store) => {
  return action$
    .ofType(actions.sendFindMyTeamVerificationEmail.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      return api
        .sendFindMyTeamVerificationEmail(email)
        .map(() => actions.sendFindMyTeamVerificationEmailSuccess(true))
        .catch(() => Observable.of(actions.sendFindMyTeamVerificationEmailFailure()))
    })
}

export const epic = combineEpics(
  checkUniqueEmail,
  updatePassword,
  updateUser,
  updateProfileWithPicture,
  updateInvitedRepPassword,
  identifyDriftUserForInvitedRep,
  fetchUserId,
  fetchUser,
  updateStagedChangesForInvitedRep,
  updateProfileWithCalendarSetting,
  updateUserProperties,
  getLoginUrl,
  checkEmailIsSSOEnabled,
  checkEmailIsSSOEnabledSuccess,
  fetchClearbitForEmail,
  findAssociatedDomain,
  sendFindMyTeamVerificationEmail,
  redirectToHyfy,
)



// WEBPACK FOOTER //
// ./src/modules/user/epic.js